/*
 * Sesiunea: tokenul de acces NUMAI in memorie; in sessionStorage sta doar ce
 * trebuie ca F5 sa nu arunce omul la login.
 *
 * Memoria e sursa citita la fiecare cerere, deci un refresh reusit se vede
 * imediat peste tot. Tokenul de acces (jwt) nu se mai scrie in storage deloc
 * (audit IAM, 16 oct 2026): un script strain ajuns in pagina il lua de acolo si
 * il folosea de pe alta masina. La F5 se cere unul nou, prin refresh.
 *
 * Refresh tokenul:
 *   - cand api-ul il pune in cookie httpOnly (`refresh_cookie: true` in
 *     raspuns), JavaScript-ul nu-l vede deloc; in storage ramane doar semnul
 *     ca sesiunea se reia prin cookie;
 *   - altfel (api-ul de azi, si carcasa nativa, unde cookie-ul ar fi al unui
 *     site strain) se tine in sessionStorage, ca pana acum - singura cale prin
 *     care F5 nu inseamna login din nou.
 *
 * sessionStorage si nu localStorage: pe o statie comuna dintr-o banca tokenul
 * trebuie sa moara odata cu fila. localStorage l-ar lasa pe disc pentru
 * urmatorul om care se aseaza la acelasi calculator.
 *
 * Modulul e in afara Reactului dinadins. Clientul de api are nevoie de token in
 * mijlocul unui `fetch`, adica intr-un loc unde nu exista hook-uri; un context
 * React ar insemna fie sa se paseze tokenul prin fiecare apel, fie o variabila
 * globala ascunsa sub un `useEffect`. Reactul se aboneaza la modulul asta prin
 * `subscribe`, nu invers.
 */

import { config } from './config';
import type { AuthTokens, MeResponse, ShopUser } from './types';

export interface SessionState {
  jwt: string | null;
  refreshToken: string | null;
  /** Refresh tokenul e in cookie httpOnly, nu in pagina. */
  refreshCookie: boolean;
  user: ShopUser | null;
  /**
   * Creste la fiecare token nou. Clientul retine generatia cu care a plecat o
   * cerere; daca la 401 generatia s-a schimbat intre timp, tokenul a fost deja
   * improspatat de altcineva si cererea se reia direct, fara alt refresh.
   */
  generation: number;
}

/*
 * `can` de la `GET /shop/auth/me` NU se pastreaza aici.
 *
 * Singurul lui cititor era meniul de aprobari, iar acela a disparut: aprobarea
 * e o mutare de stare pe tichet, in platforma. Un drept oglindit in sesiune si
 * in sessionStorage pe care nu-l citeste nimeni e o valoare care se poate
 * invechi fara sa observe cineva, deci nu se mai tine.
 */
let state: SessionState = {
  jwt: null,
  refreshToken: null,
  refreshCookie: false,
  user: null,
  generation: 0,
};

/* -------------------------------------------------------------- storage */

/* Un browser cu storage blocat (mod privat strict, politica de grup pe statie)
   nu are voie sa doboare aplicatia. Se merge doar cu memoria. */
function safeStorage(): Storage | null {
  try {
    const probe = '__catalog_probe__';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

const storage = safeStorage();

/* Fara jwt. O oglinda veche (cu `jwt`) se citeste mai departe, dar jwt-ul
   din ea se ignora - sesiunea se reia prin refresh. */
interface StoredSession {
  refreshToken: string | null;
  refreshCookie?: boolean;
  user: ShopUser | null;
}

function persist(): void {
  if (!storage) return;
  try {
    const stored: StoredSession = {
      refreshToken: state.refreshCookie ? null : state.refreshToken,
      refreshCookie: state.refreshCookie,
      user: state.user,
    };
    storage.setItem(config.sessionStorageKey, JSON.stringify(stored));
  } catch {
    /* Cota depasita nu e motiv sa pice ecranul; sesiunea ramane in memorie. */
  }
}

/* ------------------------------------------------------------ abonati */

type Listener = (next: SessionState) => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener(state);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Instantaneu stabil pentru `useSyncExternalStore`.
 *
 * Se intoarce chiar obiectul `state`, nu o copie: React compara referinta, iar
 * o copie noua la fiecare citire ar declansa redesenare la infinit.
 */
export function getSnapshot(): SessionState {
  return state;
}

/* ------------------------------------------------------------- operatii */

/**
 * Reia sesiunea din oglinda. Intoarce true daca exista cu ce cere un jwt nou
 * (refresh token sau cookie); jwt-ul il aduce apoi `resume()` din client.
 */
export function restore(): boolean {
  if (!storage) return false;
  try {
    const raw = storage.getItem(config.sessionStorageKey);
    if (!raw) return false;
    const stored = JSON.parse(raw) as Partial<StoredSession> | null;
    const refreshCookie = stored?.refreshCookie === true;
    const refreshToken =
      typeof stored?.refreshToken === 'string' && stored.refreshToken !== ''
        ? stored.refreshToken
        : null;
    if (!refreshCookie && refreshToken === null) return false;
    state = {
      jwt: null,
      refreshToken: refreshCookie ? null : refreshToken,
      refreshCookie,
      user: stored?.user ?? null,
      generation: state.generation + 1,
    };
    /* Rescrie oglinda fara jwt: una veche, cu tokenul de acces in ea, nu mai
       ramane in storage pana la inchiderea filei. */
    persist();
    emit();
    return true;
  } catch {
    /* Oglinda stricata se ignora; se merge la login ca si cum n-ar fi existat. */
    return false;
  }
}

/**
 * Preia corpul intors de login / refresh / sso-exchange. Contractul spune ca
 * sunt identice, deci exista o singura functie care il ia.
 */
export function adopt(tokens: AuthTokens): void {
  const refreshCookie = tokens.refresh_cookie === true;
  state = {
    ...state,
    jwt: tokens.jwt,
    refreshToken: refreshCookie ? null : tokens.refresh_token || null,
    refreshCookie,
    user: tokens.user ?? state.user,
    generation: state.generation + 1,
  };
  persist();
  emit();
}

/**
 * Cine sunt, de la `GET /shop/auth/me`. Ruta intoarce si `can`, dar nu se
 * pastreaza: nu-l mai citeste niciun ecran.
 */
export function setIdentity(me: MeResponse): void {
  state = {
    ...state,
    user: me.user ?? state.user,
  };
  persist();
  emit();
}

export function clear(): void {
  state = {
    jwt: null,
    refreshToken: null,
    refreshCookie: false,
    user: null,
    generation: state.generation + 1,
  };
  if (storage) {
    try {
      storage.removeItem(config.sessionStorageKey);
    } catch {
      /* Nimic de facut; memoria e deja goala, iar ea e sursa citita. */
    }
  }
  emit();
}

export function getToken(): string | null {
  return state.jwt;
}

export function getRefreshToken(): string | null {
  return state.refreshToken;
}

/** Refresh tokenul e in cookie httpOnly: refresh-ul si logout-ul il duc singure. */
export function usesRefreshCookie(): boolean {
  return state.refreshCookie;
}

/** Exista cu ce cere un jwt nou. */
export function canRefresh(): boolean {
  return state.refreshCookie || state.refreshToken !== null;
}

export function getGeneration(): number {
  return state.generation;
}

export function isAuthenticated(): boolean {
  return state.jwt !== null;
}
